import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, Heading, Text, Grid, Flex,
  Button, Skeleton, Stack, Select
} from "@chakra-ui/react";
import useArticleStore from "../stores/useArticleStore";
import useFeedStore from "../stores/useFeedStore";
import { supabase } from "../integrations/supabase/client";
import CategoryCard from "../components/articles/CategoryCard";

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("published_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const { categories } = useFeedStore();
  
  useEffect(() => {
    // 确保分类ID是URL解码的
    const decodedCategory = decodeURIComponent(categoryId);
    
    const fetchArticlesByCategory = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 获取所有feed
        const { data: feedsData, error: feedsError } = await supabase
          .from('feed')
          .select('feed_id')
          .eq('category', decodedCategory);
          
        if (feedsError) throw feedsError;
        
        // If no matching feeds found
        if (!feedsData || feedsData.length === 0) {
          // Due to limited data in debug phase, fetch all articles directly
          const { data, error } = await supabase
            .from('article')
            .select('*')
            .order(sortBy, { ascending: sortOrder === 'asc' });

          if (error) throw error;

          // Add category information
          const articlesWithCategory = data.map(article => ({
            ...article,
            category: decodedCategory
          }));

          setArticles(articlesWithCategory);
          setIsLoading(false);
          return;
        }
        
        // 提取feed_id列表
        const feedIds = feedsData.map(feed => feed.feed_id);
        
        // 查询匹配feed_id的文章
        const { data, error } = await supabase
          .from('article')
          .select('*')
          .in('feed_id', feedIds)
          .order(sortBy, { ascending: sortOrder === 'asc' });
          
        if (error) throw error;
        
        setArticles(data);
      } catch (error) {
        console.error('Error fetching articles by category:', error);
        setError(error.message || "Error loading category articles");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchArticlesByCategory();
  }, [categoryId, sortBy, sortOrder]);
  
  // Get category color
  const getCategoryColor = (category) => {
    switch(category) {
      case 'AI行业动态': return "blue.500";
      case 'Web3动态': return "orange.500";
      case '企业家动态': return "green.500";
      case '投资人动态': return "purple.500";
      case '一人公司动态': return "teal.500";
      case '科技': return "green.500";
      default: return "purple.500";
    }
  };
  
  // Ensure category ID is URL decoded
  const decodedCategory = decodeURIComponent(categoryId);
  
  if (isLoading) {
    return (
      <Box maxWidth="1200px" mx="auto" py={8} px={4}>
        <Skeleton height="50px" width="200px" mb={6} />
        <Stack spacing={4}>
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} height="200px" />
          ))}
        </Stack>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box maxWidth="1200px" mx="auto" py={8} px={4} textAlign="center">
        <Heading as="h1" mb={4} color="red.500">
          Category Loading Failed
        </Heading>
        <Text>{error}</Text>
      </Box>
    );
  }
  
  return (
    <Box maxWidth="1200px" mx="auto" py={8} px={4}>
      {/* Filter and sort controls */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        flexWrap="wrap"
        gap={4}
      >
        <Text fontWeight="bold">
          Found {articles.length} articles
        </Text>
        
        <Flex gap={4}>
          <Select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            size="sm"
            width="auto"
          >
            <option value="published_at">By Published Date</option>
            <option value="title">By Title</option>
          </Select>

          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            size="sm"
            width="auto"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </Select>
        </Flex>
      </Flex>
      
      {/* Article list */}
      {articles.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text fontSize="lg">No articles in this category</Text>
        </Box>
      ) : (
        <Grid 
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)"
          }}
          gap={6}
        >
          {articles.map(article => (
            <Box
              key={article.id}
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              p={5}
              boxShadow="sm"
              transition="all 0.2s"
              _hover={{ boxShadow: "md" }}
            >
              <Heading as="h3" size="md" mb={3} noOfLines={2}>
                <Box 
                  as="a" 
                  href={`/article/${article.article_id}`} 
                  _hover={{ color: getCategoryColor(decodedCategory) }}
                >
                  {article.title}
                </Box>
              </Heading>
              
              <Text noOfLines={3} color="gray.600" mb={4}>
                {article.description}
              </Text>
              
              <Flex justify="space-between" fontSize="sm" color="gray.500">
                <Text>{article.author || 'Anonymous'}</Text>
                <Text>{new Date(article.published_at).toLocaleDateString()}</Text>
              </Flex>
            </Box>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CategoryPage; 